import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ScrollText, Printer, Loader2 } from "lucide-react";
import LegalMarkdown from "@/components/LegalMarkdown";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TermsPage() {
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/legal/current/terms`);
        if (alive) setDoc(data);
      } catch (e) {
        if (alive) setDoc({ error: true });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#EDF7F6] to-[#FAF9F6]">
      <header className="bg-white border-b border-[#E2E8F0] shadow-sm print:hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button
              data-testid="terms-back-btn"
              onClick={() => navigate("/")}
              variant="ghost"
              className="text-primary hover:bg-primary/5"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <button
              data-testid="terms-brand-home"
              onClick={() => navigate("/get-started")}
              className="flex items-center gap-3"
              aria-label="Explore care types"
            >
              <img
                src="/tyvena-lockup-horizontal.png"
                alt="Tyvena"
                className="h-10 md:h-11 w-auto rounded-lg shadow-md"
              />
            </button>
          </div>
          <Button
            data-testid="terms-print-btn"
            onClick={() => window.print()}
            variant="outline"
            className="rounded-full font-body"
          >
            <Printer className="w-4 h-4 mr-2" /> Print
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-6">
            <ScrollText className="w-4 h-4 text-primary" />
            <span className="text-sm font-body font-semibold text-primary">Terms of Service</span>
          </div>
          {doc && !doc.error && (
            <div
              data-testid="terms-version-chip"
              className="mt-2 text-sm text-text-secondary font-body flex flex-wrap gap-x-6 gap-y-1"
            >
              <span><span className="font-semibold">Version:</span> {doc.version}</span>
              <span><span className="font-semibold">Effective:</span> {doc.effective_date}</span>
              {doc.published_at && (
                <span><span className="font-semibold">Published:</span> {new Date(doc.published_at).toLocaleDateString()}</span>
              )}
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center gap-3 text-text-secondary font-body py-16" data-testid="terms-loading">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading the latest version…
          </div>
        ) : doc?.error ? (
          <p data-testid="terms-error" className="text-text-secondary font-body py-16">
            We couldn't load the Terms right now. Please try again in a moment.
          </p>
        ) : (
          <article className="bg-white rounded-2xl border border-[#E2E8F0] p-6 md:p-10 shadow-sm">
            <LegalMarkdown body={doc.body_markdown} />
          </article>
        )}

        <div className="mt-12 pt-8 border-t border-[#E2E8F0] flex flex-wrap items-center gap-3 justify-between print:hidden">
          <a href="/legal/privacy" data-testid="terms-to-privacy" className="text-sm font-body text-primary hover:underline">
            → Read our Privacy Notice
          </a>
          <p className="text-xs text-text-secondary font-body">© {new Date().getFullYear()} Tyvena · A guide for the care journey</p>
        </div>
      </main>
    </div>
  );
}
